import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import apiClient from './lib/api';
import { CURRENCIES, formatPrice as formatPriceFn, formatThreshold as formatThresholdFn } from './lib/currency';
import { LANGUAGES, createT } from './lib/i18n';
import { trackAddToCart } from './lib/analytics';
import { Loader2 } from 'lucide-react';
import { MarketplaceModeProvider, useMarketplaceMode } from './contexts/MarketplaceModeContext';

// Critical components - loaded immediately
import Header from './components/Header';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import SkipToContent from './components/SkipToContent';
import ImpersonationBanner from './components/ImpersonationBanner';
import PerformanceMonitor from './components/PerformanceMonitor';
import AuthCallback from './components/AuthCallback';
import ErrorBoundary from './components/ErrorBoundary';
import { ScrollProgressBar } from './components/animations';
import ScrollToTopButton from './components/ScrollToTopButton';
import PageTransition from './components/PageTransition';
import CookieConsent from './components/CookieConsent';
import NewsletterCapture from './components/NewsletterCapture';

// Pages - lazy loaded for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePage = lazy(() => import('./pages/CookiePage'));
// DashboardPage removed 2026-04-28 — replaced by CustomerDashboard at /customer/*.
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
// VendorDashboard removed 2026-04-28 — see /vendor/* route below.
const MakesPage = lazy(() => import('./pages/MakesPage'));
const MakeDetailPage = lazy(() => import('./pages/MakeDetailPage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const StandaloneMessagesPage = lazy(() => import('./pages/MessagesPage').then(m => ({ default: m.StandaloneMessagesPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const OEMLandingPage = lazy(() => import('./pages/OEMLandingPage'));
const CategoryLandingPage = lazy(() => import('./pages/CategoryLandingPage'));
const PartsLandingPage = lazy(() => import('./pages/PartsLandingPage'));

// Export API client for use in pages
export { apiClient };
export const API = ''; // Base path is handled by apiClient

// Re-export marketplace mode hook so pages can import it from App
export { useMarketplaceMode };

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext) || { user: null, login: async () => {}, register: async () => {}, logout: async () => {}, googleLogin: async () => {}, checkAuth: async () => {}, loading: true };

// Decode the `exp` claim from a JWT without verifying signature (browser side).
// Returns Unix-seconds exp, or null if the token is malformed.
const decodeJwtExp = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;
    const part = token.split('.')[1];
    if (!part) return null;
    // base64url → base64
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(part.length + (4 - part.length % 4) % 4, '=');
    const json = JSON.parse(decodeURIComponent(escape(atob(b64))));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch (_e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Single proactive-refresh timer shared across the provider.
  // Refreshes the access token at (exp - 60s) so 401s never happen on the
  // happy path. The HTTP client's reactive 401 interceptor stays as a
  // belt-and-braces fallback.
  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback((token) => {
    clearRefreshTimer();
    const exp = decodeJwtExp(token);
    if (!exp) return;
    const nowSec = Math.floor(Date.now() / 1000);
    // Refresh 60 seconds before expiry; if the token is already that close
    // to dying or expired, refresh immediately on the next tick.
    const delayMs = Math.max(1_000, (exp - nowSec - 60) * 1000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await apiClient.post('/auth/refresh/');
        const newToken = res?.data?.token;
        if (newToken) {
          localStorage.setItem('token', newToken);
          scheduleProactiveRefresh(newToken);  // chain
        }
      } catch (_e) {
        // Refresh failed — token is dead. Don't redirect from here; the
        // reactive 401 interceptor will redirect on the next user action.
        clearRefreshTimer();
      }
    }, delayMs);
  }, [clearRefreshTimer]);

  // Re-schedule whenever the page becomes visible (sleep/wake/tab switch
  // can leave the timer paused on some browsers).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem('token');
      if (token) scheduleProactiveRefresh(token);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearRefreshTimer();
    };
  }, [scheduleProactiveRefresh, clearRefreshTimer]);

  const checkAuth = useCallback(async () => {
    // Check if we have a token and user in localStorage first
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }

    // Validate with backend only if we have a token — skip for anonymous
    // visitors to avoid a 401 network error in the browser console.
    if (token) {
      try {
        const response = await apiClient.get('/auth/me');
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        // Schedule proactive refresh now that we know the token is valid.
        scheduleProactiveRefresh(token);
      } catch (error) {
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearRefreshTimer();
      }
    }
    setLoading(false);
  }, [scheduleProactiveRefresh, clearRefreshTimer]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Pass guest session_id as query param so backend can merge the guest cart on login
    const sessionId = localStorage.getItem('cart_session');
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await apiClient.post('/auth/login/', { email, password }, { params });
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    scheduleProactiveRefresh(response.data.token);
    // Guest cart has been merged server-side — drop the guest session id so
    // subsequent cart reads use the authenticated user's cart.
    if (sessionId) {
      localStorage.removeItem('cart_session');
    }
    return response.data;
  };

  const register = async (firstName, lastName, email, password) => {
    // P0: Pass guest session_id so backend merges the guest cart into the new user's cart
    const sessionId = localStorage.getItem('cart_session');
    const params = sessionId ? { session_id: sessionId } : {};
    const response = await apiClient.post(
      '/auth/register/',
      { first_name: firstName, last_name: lastName, email, password },
      { params }
    );
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    scheduleProactiveRefresh(response.data.token);
    if (sessionId) {
      localStorage.removeItem('cart_session');
    }
    return response.data;
  };

  const logout = async (currentPath) => {
    try {
      await apiClient.post('/auth/logout/', {});
    } catch (error) {
      // Logout should succeed client-side even if API call fails
      console.warn('Logout API call failed:', error.message);
    }
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('cart_session');
    clearRefreshTimer();

    // Determine redirect: stay on public pages, redirect to /login from protected pages
    const publicRoutes = ['/', '/search', '/makes', '/product', '/cart', '/checkout',
                          '/terms', '/privacy', '/about', '/delivery', '/returns',
                          '/payment', '/contact', '/help', '/shipping', '/faq', '/warranty',
                          '/category', '/forgot-password', '/reset-password'];
    const path = currentPath || window.location.pathname;
    const isPublicPath = publicRoutes.some(route => path === route || path.startsWith(route + '/'));

    if (!isPublicPath) {
      window.location.href = '/login';
    }
  };

  const googleLogin = async (sessionId) => {
    const response = await apiClient.post('/auth/google/callback', { session_id: sessionId });
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    scheduleProactiveRefresh(response.data.token);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext) || { cart: { items: [], subtotal: 0, item_count: 0 }, addToCart: async () => {}, removeFromCart: async () => {}, updateQuantity: async () => {}, clearCart: async () => {}, fetchCart: async () => {}, cartLoading: false, loading: false, sessionId: '' };

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [sessionId] = useState(() => localStorage.getItem('cart_session') || `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [cartLoading, setCartLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    localStorage.setItem('cart_session', sessionId);
  }, [sessionId]);

  const fetchCart = useCallback(async () => {
    // Don't fetch cart until auth is initialized
    if (authLoading) {
      return;
    }
    try {
      setCartLoading(true);
      const params = user ? {} : { session_id: sessionId };
      const response = await apiClient.get('/cart', { params });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setCartLoading(false);
    }
  }, [user, sessionId, authLoading]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (listingId, quantity = 1, listing = null) => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await apiClient.post('/cart/add', { listing_id: listingId, quantity }, { params });
      // Tracking gap fix (2026-07-20): add_to_cart only fired on ProductPage —
      // ADD buttons on home/search/landing grids added silently. Callers pass
      // the listing object and the conversion fires HERE, once, for every
      // surface (GA4 + Meta browser tag via GTM + server CAPI, event_id-deduped).
      //
      // 2026-07-31: fire BEFORE the fetchCart() round-trip, not after. The add is
      // already confirmed the moment the POST returns 200; waiting on a second
      // network call first left a window in which a re-render or a navigation
      // could intervene and the event was observed going missing. Nothing about
      // the conversion depends on the refreshed cart, so there is no reason to wait.
      if (listing) {
        try { trackAddToCart(listing, quantity); } catch (e) { /* never break ATC */ }
      }
      await fetchCart();
      // Audit Cowork-test1 (2026-04-29): callers (e.g. ProductPage) emit
      // their own item-specific toast. Don't double-fire from here.
      return true;
    } catch (error) {
      // 2026-07-31: this read `.detail` only, but the error-handler refactor moved the
      // human message to `.message` — so the backend's precise reason ("Cannot add
      // more. Only 1 available, already 1 in cart.") was being thrown away and every
      // failure surfaced as a bare "Failed to add to cart". Since used parts are all
      // qty 1, the second click on the same part ALWAYS lands here, which is exactly
      // what made add_to_cart look like it "doesn't fire" during tracking tests.
      const d = error.response?.data || {};
      toast.error(d.message || d.detail || 'Failed to add to cart');
      throw error;
    }
  };

  const updateQuantity = async (listingId, quantity) => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await apiClient.post('/cart/update', { listing_id: listingId, quantity }, { params });
      await fetchCart();
    } catch (error) {
      toast.error('Failed to update cart');
    }
  };

  const removeFromCart = async (listingId) => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await apiClient.delete(`/cart/remove/${listingId}`, { params });
      await fetchCart();
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  return (
    <CartContext.Provider value={{ cart, sessionId, cartLoading, loading: cartLoading, fetchCart, addToCart, updateQuantity, removeFromCart }}>
      {children}
    </CartContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext) || { theme: 'light', setTheme: () => {}, toggleTheme: () => {} };

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    const resolved = saved || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    // Apply synchronously inside the initializer so the class is set before the
    // first React paint. The index.html inline script already does this for the
    // very first load, but this covers subsequent context re-mounts (e.g. StrictMode
    // double-invocation in development) and fast-refresh cycles.
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    return resolved;
  });

  // Keep the class in sync whenever theme changes (toggle, system preference change).
  // We deliberately avoid remove() before add() to prevent a single-frame flash
  // where neither class is present and CSS variables fall back to their `:root`
  // (light-mode) defaults, rendering dark-mode content invisible.
  useEffect(() => {
    const root = document.documentElement;
    const toRemove = theme === 'dark' ? 'light' : 'dark';
    root.classList.remove(toRemove);
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const saved = localStorage.getItem('theme');
      if (!saved) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Currency Context
const CurrencyContext = createContext(null);

export const useCurrency = () => useContext(CurrencyContext) || { currency: 'EUR', setCurrency: () => {}, formatPrice: (n) => `€${Number(n||0).toFixed(2)}`, formatThreshold: () => '€100', showVAT: true, toggleVAT: () => {} };

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('currency') || 'EUR');
  const [showVAT, setShowVAT] = useState(() => {
    // Persist VAT preference in localStorage
    const saved = localStorage.getItem('showVAT');
    return saved === null ? true : saved === 'true';
  });
  // Bumped when live FX rates are applied, so formatPrice re-renders.
  const [fxVersion, setFxVersion] = useState(0);

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('showVAT', showVAT);
  }, [showVAT]);

  // Live ECB FX rates (no margin) + geolocation currency, once on mount.
  // Applies live rates onto CURRENCIES and auto-selects the visitor's local
  // currency (GB->GBP, NO->NOK, PL->PLN...) unless they've manually picked one.
  useEffect(() => {
    let cancelled = false;
    apiClient.get('/public/fx-rates').then((res) => {
      if (cancelled) return;
      const data = res.data || {};
      const rates = data.rates || {};
      Object.keys(rates).forEach((code) => {
        if (CURRENCIES[code] && typeof rates[code] === 'number' && rates[code] > 0) {
          CURRENCIES[code].rate = rates[code];
        }
      });
      setFxVersion((v) => v + 1);
      const detected = data.detected_currency;
      if (localStorage.getItem('currency_manual') !== '1' && detected && CURRENCIES[detected]) {
        setCurrencyState(detected);
      }
    }).catch(() => { /* keep fallback rates */ });
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((code) => {
    if (CURRENCIES[code]) {
      try { localStorage.setItem('currency_manual', '1'); } catch (e) { /* ignore */ }
      setCurrencyState(code);
    }
  }, []);

  const toggleVAT = useCallback(() => {
    setShowVAT(prev => !prev);
  }, []);

  const formatPrice = useCallback((eurAmount) => {
    // Prices in DB include VAT at the SELLER country's domestic rate — SIA Cono Group,
    // LATVIA, 21% (owner confirmed 2026-08-10; was documented as Estonian 24%).
    // If showVAT is false, remove the embedded VAT by dividing by 1.21.
    const adjustedAmount = showVAT ? eurAmount : eurAmount / 1.21;
    return formatPriceFn(adjustedAmount, currency);
  }, [currency, showVAT, fxVersion]);

  // As-charged formatter: converts currency for display but NEVER applies the
  // ex-VAT toggle division. For the checkout total, the Place Order button and the
  // order receipt — with the VAT toggle off those showed a total 19.35% below what
  // Stripe actually took (pricing audit 2026-08-10, P0). Item prices elsewhere keep
  // honouring the toggle; the amount being charged does not.
  const formatPriceCharged = useCallback((eurAmount) => {
    return formatPriceFn(eurAmount, currency);
  }, [currency, fxVersion]);

  const formatThreshold = useCallback(() => formatThresholdFn(currency), [currency]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, formatPriceCharged, formatThreshold, showVAT, toggleVAT }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext) || { language: 'en', setLanguage: () => {}, t: (key) => key };

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Only 8 of the 22 translation sets are exposed in LANGUAGES; a stored code outside it
    // (e.g. 'no' from an earlier build or manual set) crashed Header on LANGUAGES[language].flag.
    const stored = localStorage.getItem('language');
    return stored && LANGUAGES[stored] ? stored : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (LANGUAGES[code]) setLanguageState(code);
  }, []);

  const t = useMemo(() => createT(language), [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Loading Component for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      <p className="text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

// Error Fallback Component
const ErrorFallback = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center">
            <span className="text-4xl">⚠️</span>
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="font-heading text-3xl uppercase font-bold">Something Went Wrong</h1>
          <p className="text-muted-foreground">
            We encountered an error while loading this page. Please try again.
          </p>
        </div>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-accent text-accent-foreground font-bold rounded-none hover:bg-accent/90 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 border-2 border-border font-bold rounded-none hover:bg-muted transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles, adminRoles, requireMultiVendor = false }) => {
  const { user, loading } = useAuth();
  const { isMultiVendor, loading: modeLoading } = useMarketplaceMode();
  const location = useLocation();

  if (loading || modeLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Scoped admin_role allowance (e.g. marketing_manager into /admin) — applies ONLY
  // when the route opts in via adminRoles, so it never leaks into seller/other routes.
  const adminRoleAllowed = adminRoles && adminRoles.includes(user.admin_role);
  if (roles && !roles.includes(user.role) && !adminRoleAllowed) {
    return <Navigate to="/" replace />;
  }

  // Gate seller-only routes in single_vendor mode (unless user is admin)
  if (requireMultiVendor && !isMultiVendor) {
    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    if (!isAdmin) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  const { loading: authLoading } = useAuth();
  const { cartLoading } = useCart();

  // Handle OAuth callback - check URL fragment for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show loader while critical contexts are initializing
  // Only block on auth loading, cart can load in background
  if (authLoading) {
    return <PageLoader />;
  }

  // Check if current route is a dashboard route (no header/footer needed)
  // Note: /dashboard and /customer need header/footer (no built-in nav)
  const isDashboardRoute = location.pathname.startsWith('/admin') ||
                          location.pathname.startsWith('/seller') ||
                          location.pathname.startsWith('/vendor');

  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <ScrollToTop />
      <SkipToContent />
      <PerformanceMonitor />
      <ScrollProgressBar />
      {!isDashboardRoute && <Header />}
      <main id="main-content" className="flex-1 pb-safe lg:pb-0">
        <PageTransition>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <HomePage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/search" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <SearchPage />
              </Suspense>
            </ErrorBoundary>
          } />
          {/* Dedicated category landing pages — rich content, SEO-targeted.
              Replaces the old SearchPage fallback for /category/:slug.
              SearchPage remains at /search for free-text queries. */}
          <Route path="/category/:slug" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <CategoryLandingPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/makes" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <MakesPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/makes/:makeId" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <MakeDetailPage />
              </Suspense>
            </ErrorBoundary>
          } />
          {/* SEO-friendly canonical URL: /product/{slug}/{listingId} */}
          <Route path="/product/:slug/:listingId" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProductPage />
              </Suspense>
            </ErrorBoundary>
          } />
          {/* Legacy URL — still works. ProductPage does a history.replace to canonical once it has the slug. */}
          <Route path="/product/:listingId" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProductPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/cart" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <CartPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/checkout" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <CheckoutPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/order/success" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <OrderSuccessPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/login" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/register" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <RegisterPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/forgot-password" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ForgotPasswordPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/reset-password" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ResetPasswordPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/terms" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <TermsPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/privacy" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <PrivacyPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/cookies" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <CookiePage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/unsubscribe" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <UnsubscribePage />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Info/static pages */}
          {['about', 'delivery', 'returns', 'payment', 'contact', 'help', 'shipping', 'faq', 'warranty', 'imprint'].map(key => (
            <Route key={key} path={`/${key}`} element={
              <ErrorBoundary fallback={<ErrorFallback />}>
                <Suspense fallback={<PageLoader />}>
                  <InfoPage pageKey={key} />
                </Suspense>
              </ErrorBoundary>
            } />
          ))}

          {/* Protected routes */}
          {/* /dashboard/* superseded by /customer/* (2026-04-28). The legacy DashboardPage
              had hardcoded empty states for /dashboard/favorites and /dashboard/messages.
              Redirect every /dashboard URL to the modern /customer dashboard. */}
          <Route path="/dashboard" element={<Navigate to="/customer" replace />} />
          <Route path="/dashboard/orders" element={<Navigate to="/customer/orders" replace />} />
          <Route path="/dashboard/favorites" element={<Navigate to="/customer/favorites" replace />} />
          <Route path="/dashboard/messages" element={<Navigate to="/customer/messages" replace />} />
          <Route path="/dashboard/settings" element={<Navigate to="/customer/settings" replace />} />
          <Route path="/dashboard/*" element={<Navigate to="/customer" replace />} />
          <Route path="/customer/*" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <CustomerDashboard />
                </ProtectedRoute>
              </Suspense>
            </ErrorBoundary>
          } />
          {/* /vendor/* removed 2026-04-28 — duplicate of /seller/*. SellerDashboard is canonical. */}
          <Route path="/vendor/*" element={<Navigate to="/seller" replace />} />
          <Route path="/seller/*" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute roles={['seller', 'admin', 'super_admin']}>
                  <SellerDashboard />
                </ProtectedRoute>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/messages" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/messages/:conversationId" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <StandaloneMessagesPage />
                </ProtectedRoute>
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/admin/*" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute roles={['admin', 'super_admin']} adminRoles={['marketing_manager']}>
                  <AdminDashboard />
                </ProtectedRoute>
              </Suspense>
            </ErrorBoundary>
          } />
          {/* OEM landing pages — SEO-targeted, one per OEM number */}
          <Route path="/oem/:slug" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <OEMLandingPage />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Programmatic gen×category pages — 8,849 surfaces, staging only */}
          <Route path="/parts/:genSlug/:catSlug" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <PartsLandingPage />
              </Suspense>
            </ErrorBoundary>
          } />

          {/* Convenience redirects */}
          <Route path="/account" element={<Navigate to="/customer" replace />} />
          <Route path="/parts" element={<Navigate to="/search" replace />} />

          <Route path="*" element={
            <ErrorBoundary fallback={<ErrorFallback />}>
              <Suspense fallback={<PageLoader />}>
                <NotFound />
              </Suspense>
            </ErrorBoundary>
          } />
        </Routes>
        </PageTransition>
      </main>
      {!isDashboardRoute && <Footer />}
      {!isDashboardRoute && <MobileBottomNav />}
      <ScrollToTopButton />
    </div>
  );
}

// HARD_NAV interceptor (2026-08-13). When tracking-config.js sets HARD_NAV, clicks on
// product / cart / checkout links become FULL page loads instead of SPA transitions.
// Why: browser tracking inspectors (Meta Ads Data Advisor, Pixel Helper) scope
// "events on this page" per document load — on SPA navigations they display nothing,
// which repeatedly read as "tracking is broken" during manual verification even
// though every event demonstrably reached Meta. A capture-phase listener on document
// runs before React Router's delegated handler; stopPropagation keeps the Link from
// doing an SPA transition, then location.assign performs the real load. The flag is
// read at CLICK time, so flipping tracking-config.js (static file, no rebuild)
// changes behaviour immediately. Modified clicks (new-tab etc.) pass through
// untouched.
const HARD_NAV_PATH = /^\/(product\/|cart(\?|$)|checkout(\?|$))/;
function useHardNavInterceptor() {
  useEffect(() => {
    const onClick = (e) => {
      try {
        if (!(window.__ZIVU_TRACKING__ || {}).HARD_NAV) return;
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (!a) return;
        if (a.target && a.target !== '_self') return;
        const href = a.getAttribute('href') || '';
        if (!HARD_NAV_PATH.test(href)) return;
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(href);
      } catch (_err) { /* never break navigation */ }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}

function HardNavInterceptor() {
  useHardNavInterceptor();
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <HardNavInterceptor />
      <ErrorBoundary>
        <MarketplaceModeProvider>
          <ThemeProvider>
            <LanguageProvider>
              <CurrencyProvider>
                <AuthProvider>
                  <CartProvider>
                    <AppRouter />
                    <CookieConsent />
                    {/* DISABLED 2026-08-24 for the Google Merchant Center misrepresentation review.
                        Google's checklist flags interstitials shown to shoppers during review.
                        RESTORE after reinstatement by uncommenting the line below. */}
                    {/* <NewsletterCapture /> */}
                    <Toaster
                      position="bottom-right"
                      expand={true}
                      richColors={true}
                      closeButton={true}
                      duration={3000}
                      gap={8}
                      toastOptions={{
                        style: { fontFamily: "'Manrope', sans-serif" },
                      }}
                    />
                  </CartProvider>
                </AuthProvider>
              </CurrencyProvider>
            </LanguageProvider>
          </ThemeProvider>
        </MarketplaceModeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
